import { useEffect, useState } from "react";
import { toast } from "sonner";
import type { ContentPublishingConnector } from "@/lib/composerCore";
import { CONNECTOR_TYPE_LABELS, type EditorState } from "../connectorManagerHelpers";

const READY_DURATION_KEY = "excerptus:connector-ready-duration-seconds";
export const READY_DURATION_OPTIONS = [2, 4, 8, 15, 30, 0] as const;

export type OpenIndicator = {
  connectorId: string;
  phase: "opening" | "ready";
} | null;

type Args = {
  connectors: ContentPublishingConnector[];
  editor: EditorState;
  openEdit: (connector: ContentPublishingConnector) => void;
};

/** Owns the connector open-edit lifecycle: window-event listener that
 *  opens a connector by id, the "Opening… → Editor ready" indicator,
 *  the auto-clear timer, the persisted ready-duration preference, and
 *  the per-connector live-region announcement. */
export const useConnectorOpenIndicator = ({ connectors, editor, openEdit }: Args) => {
  const [openIndicator, setOpenIndicator] = useState<OpenIndicator>(null);
  const [pendingOpenConfirm, setPendingOpenConfirm] = useState<string | null>(null);
  const [readyDurationSec, setReadyDurationSec] = useState<number>(() => {
    if (typeof window === "undefined") return 4;
    const raw = window.localStorage.getItem(READY_DURATION_KEY);
    const parsed = raw === null ? NaN : Number(raw);
    return Number.isFinite(parsed) && (READY_DURATION_OPTIONS as readonly number[]).includes(parsed)
      ? parsed
      : 4;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(READY_DURATION_KEY, String(readyDurationSec));
  }, [readyDurationSec]);

  // Auto-clear the "ready" indicator after the configured delay.
  useEffect(() => {
    if (openIndicator?.phase !== "ready") return;
    if (readyDurationSec <= 0) return;
    const t = window.setTimeout(() => {
      setOpenIndicator((cur) =>
        cur && cur.connectorId === openIndicator.connectorId && cur.phase === "ready" ? null : cur,
      );
    }, readyDurationSec * 1000);
    return () => window.clearTimeout(t);
  }, [openIndicator, readyDurationSec]);

  // Cross-component "open this connector" requests (e.g. dispatch failure
  // callout's "Go to Kit connector" link).
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ connectorId?: string }>).detail;
      const id = detail?.connectorId;
      if (!id) return;
      const target = connectors.find((c) => c.id === id);
      if (!target) {
        toast.error("Connector not found in this workspace.", {
          id: `connector-open-error-${id}`,
        });
        return;
      }
      requestAnimationFrame(() => {
        const node = document.getElementById(`connector-${id}`);
        node?.scrollIntoView({ behavior: "smooth", block: "center" });
        openEdit(target);
        toast.success(`Opening "${target.display_name}"…`, {
          id: `connector-opening-${id}`,
        });
        setOpenIndicator({ connectorId: id, phase: "opening" });
        setPendingOpenConfirm(id);
      });
    };
    window.addEventListener("excerptus:open-connector-edit", handler as EventListener);
    return () => {
      window.removeEventListener("excerptus:open-connector-edit", handler as EventListener);
    };
  }, [connectors, openEdit]);

  // Once the editor reflects the pending connector being open, fire the
  // "Editor ready" toast.
  useEffect(() => {
    if (!pendingOpenConfirm) return;
    if (editor.open && editor.connector?.id === pendingOpenConfirm) {
      toast.success(
        `Editor ready — re-test or update the secret for "${editor.connector.display_name}".`,
        { id: `connector-ready-${pendingOpenConfirm}` },
      );
      setOpenIndicator({ connectorId: pendingOpenConfirm, phase: "ready" });
      setPendingOpenConfirm(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingOpenConfirm, editor.open, editor.connector?.id]);

  // Specific live-region message tied to the connector by name.
  const liveAnnouncement = (() => {
    if (!openIndicator) return "";
    const c = connectors.find((x) => x.id === openIndicator.connectorId);
    if (!c) return "";
    const typeLabel = CONNECTOR_TYPE_LABELS[c.connector_type] ?? c.connector_type;
    return openIndicator.phase === "opening"
      ? `Opening editor for ${c.display_name} (${typeLabel} connector)…`
      : `Editor ready for ${c.display_name} (${typeLabel} connector). You can re-test the connection or update the secret.`;
  })();

  return {
    openIndicator,
    setOpenIndicator,
    readyDurationSec,
    setReadyDurationSec,
    liveAnnouncement,
  };
};
